import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { blogPostQuery } from "@/lib/blog/queries";
import { ArticleBody, extractHeadings } from "@/components/blog/ArticleBody";
import { BlogCard, formatBlogDate } from "@/components/blog/BlogCard";
import { SubscribeForm } from "@/components/blog/SubscribeForm";
import { canonicalUrl } from "@/lib/seo/site";
import { PostNotFound } from "@/components/blog/PostNotFound";
import { journalMetadata } from "@/lib/blog/seo";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ context, params }) => {
    const detail = await context.queryClient.ensureQueryData(blogPostQuery(params.slug));
    if (!detail) throw notFound();
    return journalMetadata(detail.post);
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Unavailable — CB16 Journal" }, { name: "robots", content: "noindex" }] };
    }
    const url = canonicalUrl(`/blog/${loaderData.slug}`);
    const meta = [
      { title: loaderData.title },
      { name: "description", content: loaderData.description },
      { property: "og:type", content: "article" },
      { property: "og:title", content: loaderData.title },
      { property: "og:description", content: loaderData.description },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
    ];
    if (loaderData.shareImage) {
      meta.push({ property: "og:image", content: loaderData.shareImage });
      meta.push({ name: "twitter:image", content: loaderData.shareImage });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.headline,
            description: loaderData.description,
            image: loaderData.image.startsWith("/") && !loaderData.image.startsWith("//")
              ? canonicalUrl(loaderData.image) : loaderData.shareImage,
            datePublished: loaderData.publishedAt || undefined,
            author: loaderData.author,
            publisher: { "@type": "Organization", name: "CB16" },
            mainEntityOfPage: url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://cb16.in/" },
              { "@type": "ListItem", position: 2, name: "Journal", item: canonicalUrl("/blog") },
              { "@type": "ListItem", position: 3, name: loaderData.headline, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => <PostNotFound />,
  errorComponent: () => <PostNotFound />,
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(blogPostQuery(slug));
  if (!data) return <PostNotFound />;
  const { post, prev, next, related } = data;
  const headings = extractHeadings(post.content);
  const shareUrl = `https://cb16.in/blog/${post.slug}`;
  const shares = [
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(post.title + " " + shareUrl)}` },
    { label: "X", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <main className="bg-background">
      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6 lg:py-20">
          <nav aria-label="Breadcrumb" className="text-[10px] uppercase tracking-[0.24em] text-foreground/45">
            <Link to="/" className="hover:text-[#C6A15B]">Home</Link>
            <span className="px-2">/</span>
            <Link to="/blog" className="hover:text-[#C6A15B]">Journal</Link>
          </nav>
          <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#C6A15B]">
            {post.categoryName}
          </p>
          <h1
            className="mt-4 text-3xl font-normal leading-tight text-foreground lg:text-[42px]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {post.title}
          </h1>
          <p className="mt-5 text-[12px] uppercase tracking-[0.18em] text-foreground/50">
            {post.authorName} · {formatBlogDate(post.publishedAt)} · {post.readingMinutes} min read
          </p>
        </div>
        {post.featuredImage && (
          <div className="mx-auto max-w-5xl px-4 pb-12 sm:px-6">
            <img src={post.featuredImage} alt={post.title} className="aspect-[16/8] w-full object-cover" />
          </div>
        )}
      </section>

      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[240px_1fr] lg:px-10">
        <aside className="hidden lg:block">
          <div className="sticky top-28 flex flex-col gap-6">
            {headings.length > 0 && (
              <nav aria-label="Table of contents">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/45">
                  On this page
                </p>
                <ul className="flex flex-col gap-2 border-l border-border pl-4">
                  {headings.map((h) => (
                    <li key={h.id} className={h.level === 3 ? "pl-3" : ""}>
                      <a href={`#${h.id}`} className="text-[12px] leading-5 text-foreground/60 hover:text-[#C6A15B]">
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            <div>
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/45">Share</p>
              <div className="flex flex-wrap gap-2">
                {shares.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-border px-3 py-1 text-[11px] text-foreground/60 hover:border-[#C6A15B] hover:text-[#C6A15B]"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <article className="min-w-0">
          <ArticleBody content={post.content} />

          {post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="border border-border px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-foreground/55">
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-2 lg:hidden">
            {shares.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-border px-3 py-1 text-[11px] text-foreground/60"
              >
                {s.label}
              </a>
            ))}
          </div>

          <section className="mt-14 border border-border bg-card p-8 text-center">
            <h2 className="text-2xl font-normal text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
              Stay close to the atelier
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground/60">
              New essays, launches and private previews — once a month, never more.
            </p>
            <div className="mt-5">
              <SubscribeForm source="blog_page" />
            </div>
          </section>

          <nav className="mt-12 grid gap-4 border-t border-border pt-8 sm:grid-cols-2">
            {prev ? (
              <Link to="/blog/$slug" params={{ slug: prev.slug }} className="group">
                <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/45">Previous</span>
                <p className="mt-1 text-sm text-foreground group-hover:text-[#C6A15B]">{prev.title}</p>
              </Link>
            ) : <span />}
            {next && (
              <Link to="/blog/$slug" params={{ slug: next.slug }} className="group sm:text-right">
                <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/45">Next</span>
                <p className="mt-1 text-sm text-foreground group-hover:text-[#C6A15B]">{next.title}</p>
              </Link>
            )}
          </nav>
        </article>
      </div>

      {related.length > 0 && (
        <section className="border-t border-border">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-10">
            <h2 className="mb-8 text-2xl font-normal text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
              Related reading
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}